/**
 * Event portals (ClientPortal type 'event') in the client portal view.
 *
 *   EventPlan        the event plan: hero, facts, a sticky section bar and the
 *                    options in each of the five sections. The client picks
 *                    one per section (any number of acts and add-ons).
 *   EventOptionSheet one option in full: photos, videos, what's included, the
 *                    run of show and the pick button.
 *   EventBudget      what the picks add up to for the guest count.
 *   togglePickIn     the pick rules, the same ones the server applies.
 *
 * Videos are YouTube links or files the team uploaded to OneDrive. An upload
 * plays through a short-lived link the server hands out without a sign-in:
 *   GET /api/portal/public/:slug/event/:itemId/videos/:videoId/stream → { url }
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sparkles, Clock, MapPin, Music, UtensilsCrossed, Heart, Check, X, Play, Users, CalendarDays,
  Navigation, Layers, Minus, Plus, Send, MessageCircle, ChevronLeft, ChevronRight,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const E = {
  ink: '#222222', sub: '#484848', muted: '#6a6a6a', faint: '#929292', line: '#ebebeb', line2: '#dddddd',
  studio: '#f7f7f7', gold: '#b8975a', goldText: '#7d6233', pink: '#ff385c', ok: '#008a05',
  serif: "'Cormorant Garamond',Georgia,serif", sans: "'Jost',sans-serif",
};

export const EVENT_SECTIONS = [
  { key: 'theme', label: 'Themes', long: 'Creative event themes', icon: Sparkles, sub: 'The look, the dress code and the mood of the night.' },
  { key: 'flow', label: 'Flow & Agenda', long: 'Event flow & agenda', icon: Clock, sub: 'How the day runs, from pickup to the last song.' },
  { key: 'venue', label: 'Venue & Setup', long: 'Venue & setup', icon: MapPin, sub: 'Where it happens and how the space is built out.' },
  { key: 'ent', label: 'Entertainment', long: 'Entertainment & artists', icon: Music, sub: 'Acts, artists and moments that keep the energy up.' },
  { key: 'fnb', label: 'Food & Beverage', long: 'Food & beverage', icon: UtensilsCrossed, sub: 'Menus, live counters and the bar.' },
];
const SECTION = Object.fromEntries(EVENT_SECTIONS.map((s) => [s.key, s]));
/** Sections where the client can pick more than one option. */
const MULTI_PICK = new Set(['ent']);

const inr = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
const isMulti = (item) => item.addon || MULTI_PICK.has(item.section);
const lineCost = (item, guests) => (item.unit === 'included' ? 0 : item.unit === 'perGuest' ? Number(item.price || 0) * guests : Number(item.price || 0));

/** Picks after the client taps an option: one per section, any number of acts and add-ons. */
export const togglePickIn = (items, picks, item) => {
  const id = String(item._id);
  if (picks.includes(id)) return picks.filter((p) => p !== id);
  if (isMulti(item)) return [...picks, id];
  const byId = new Map(items.map((i) => [String(i._id), i]));
  return [...picks.filter((p) => {
    const other = byId.get(p);
    return !other || other.section !== item.section || isMulti(other);
  }), id];
};

const sortItems = (items) => [...(items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

// ── Small pieces ─────────────────────────────────────────────────────────────
const PriceLine = ({ item, size = 15 }) => (item.unit === 'included'
  ? <span style={{ fontSize: size }}><b>Included</b></span>
  : <span style={{ fontSize: size }}><b>{inr(item.price)}</b> <span style={{ color: E.muted }}>{item.unit === 'perGuest' ? 'per guest' : 'flat'}</span></span>);

const Tag = ({ children, gold }) => (
  <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 40, background: gold ? '#f8f2e7' : E.studio, color: gold ? E.goldText : E.ink, fontSize: 12, fontWeight: 500, fontFamily: E.sans }}>{children}</span>
);

const Photo = ({ item, idx = 0, children, ratio = '1 / 0.95', radius = 12, onClick }) => {
  const src = item.images?.[idx];
  const Icon = SECTION[item.section]?.icon || Sparkles;
  return (
    <div onClick={onClick} style={{ position: 'relative', aspectRatio: ratio, borderRadius: radius, overflow: 'hidden', background: E.studio, cursor: onClick ? 'pointer' : 'default' }}>
      {src
        ? <img src={src} alt={item.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        : <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', background: 'linear-gradient(160deg,#f3e6cf,#e2cba0)', color: E.goldText }}><Icon size={40} strokeWidth={1.4} /></div>}
      {children}
    </div>
  );
};

const HeartBtn = ({ on, onClick, label }) => (
  <button type="button" aria-label={label} aria-pressed={on} onClick={(e) => { e.stopPropagation(); onClick(); }}
    style={{ position: 'absolute', top: 12, right: 12, width: 34, height: 34, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center', padding: 0 }}>
    <Heart size={26} color="#fff" fill={on ? E.pink : 'rgba(0,0,0,0.45)'} strokeWidth={2} />
  </button>
);

const PickedBadge = () => (
  <span style={{ position: 'absolute', top: 12, left: 12, display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff', borderRadius: 40, padding: '5px 10px', fontSize: 12, fontWeight: 600, boxShadow: '0 2px 8px rgba(0,0,0,.15)', fontFamily: E.sans }}>
    <Check size={13} /> Your pick
  </span>
);

const VideoBadge = ({ n }) => (n > 0 ? (
  <span style={{ position: 'absolute', bottom: 12, left: 12, display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,.65)', color: '#fff', borderRadius: 40, padding: '4px 10px', fontSize: 12, fontFamily: E.sans }}>
    <Play size={11} fill="#fff" /> {n} video{n > 1 ? 's' : ''}
  </span>
) : null);

const pickBtn = (on) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 40, padding: '0 16px', borderRadius: 8, cursor: 'pointer',
  border: `1px solid ${E.ink}`, background: on ? E.ink : '#fff', color: on ? '#fff' : E.ink, fontFamily: E.sans, fontSize: 14, fontWeight: 600,
});

// ── Videos ───────────────────────────────────────────────────────────────────
const youtubeId = (url) => {
  const m = String(url || '').match(/(?:youtube\.com\/watch\?(?:.*&)?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtube\.com\/live\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

/** One video: YouTube plays in place; an upload asks the server for a fresh link first. */
const EventVideo = ({ video, item, slug }) => {
  const [playing, setPlaying] = useState(false);
  const [url, setUrl] = useState(null);
  const [state, setState] = useState('idle');
  const ytId = video.kind === 'youtube' ? youtubeId(video.url) : null;

  const play = () => {
    if (ytId) { setPlaying(true); return; }
    setState('loading');
    fetch(`${API_BASE}/api/portal/public/${slug}/event/${item._id}/videos/${video._id}/stream`)
      .then((r) => { if (!r.ok) throw new Error('unavailable'); return r.json(); })
      .then((d) => { setUrl(d.url); setPlaying(true); setState('ready'); })
      .catch(() => setState('failed'));
  };

  const poster = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : item.images?.[0];
  return (
    <div style={{ fontFamily: E.sans }}>
      <div style={{ position: 'relative', aspectRatio: '16 / 9', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
        {playing && ytId && (
          <iframe title={video.title || item.title} src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen frameBorder="0"
            style={{ width: '100%', height: '100%', display: 'block' }} />
        )}
        {playing && url && (
          <video src={url} controls autoPlay playsInline preload="metadata" poster={item.images?.[0]} style={{ width: '100%', height: '100%', display: 'block', background: '#000' }}
            onError={() => { setPlaying(false); setState('failed'); }} />
        )}
        {!playing && (
          <button type="button" onClick={play} aria-label={`Play ${video.title || 'video'}`}
            style={{ position: 'absolute', inset: 0, border: 'none', padding: 0, cursor: 'pointer', background: poster ? `center / cover no-repeat url("${poster}")` : '#222' }}>
            <span style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.28)' }} />
            <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,.95)', display: 'grid', placeItems: 'center' }}>
              {state === 'loading'
                ? <span style={{ width: 22, height: 22, border: '2px solid #ddd', borderTopColor: E.ink, borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
                : <Play size={22} fill={E.ink} color={E.ink} style={{ marginLeft: 3 }} />}
            </span>
            {ytId && <span style={{ position: 'absolute', right: 10, bottom: 10, background: '#ff0033', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>YouTube</span>}
          </button>
        )}
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, marginTop: 8 }}>{video.title || 'Video'}</div>
      {state === 'failed' && <div style={{ fontSize: 13, color: '#c13515', marginTop: 2 }}>This video can't be played right now. Please try again shortly.</div>}
    </div>
  );
};

// ── Run of show ──────────────────────────────────────────────────────────────
const Timeline = ({ schedule, compact }) => (
  <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : `repeat(${Math.min(2, schedule.length || 1)}, 1fr)`, gap: 24 }}>
    {schedule.map((day, i) => (
      <div key={i}>
        <div style={{ fontFamily: E.serif, fontSize: 22, marginBottom: 10 }}>{day.label || `Day ${i + 1}`}</div>
        {(day.rows || []).map((r, k) => (
          <div key={k} style={{ display: 'grid', gridTemplateColumns: '78px 14px 1fr', gap: 10, alignItems: 'start', paddingBottom: 12, position: 'relative' }}>
            <span style={{ fontSize: 13, color: E.muted, paddingTop: 1 }}>{r.time}</span>
            <span style={{ position: 'relative', height: '100%' }}>
              <span style={{ position: 'absolute', left: 4, top: 5, width: 7, height: 7, borderRadius: '50%', background: E.gold }} />
              {k < day.rows.length - 1 && <span style={{ position: 'absolute', left: 7, top: 14, bottom: -8, width: 1, background: E.line2 }} />}
            </span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500 }}>{r.title}</div>
              {r.note && <div style={{ fontSize: 13, color: E.muted }}>{r.note}</div>}
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

// ── Option sheet ─────────────────────────────────────────────────────────────
export const EventOptionSheet = ({ item, idx, picked, onTogglePick, onAsk, onClose, slug, isMobile }) => {
  const [photo, setPhoto] = useState(0);
  const images = item.images || [];
  const videos = item.videos || [];
  const sec = SECTION[item.section];
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const pickLabel = picked ? 'Your pick' : item.addon ? 'Add to my event' : isMulti(item) ? 'Add to my picks' : `Pick this ${sec?.key === 'venue' ? 'venue' : sec?.key === 'theme' ? 'theme' : 'option'}`;
  return (
    <div role="dialog" aria-modal="true" aria-label={item.title} onMouseDown={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 24, fontFamily: E.sans, color: E.ink }}>
      <div onMouseDown={(e) => e.stopPropagation()}
        style={{ background: '#fff', width: isMobile ? '100%' : 'min(1040px, 100%)', maxHeight: isMobile ? '94vh' : '90vh', borderRadius: isMobile ? '16px 16px 0 0' : 16, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 6px 20px rgba(0,0,0,.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${E.line}` }}>
          <button type="button" aria-label="Close" onClick={onClose} style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><X size={18} /></button>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>{idx != null ? `Option ${String(idx + 1).padStart(2, '0')} · ` : ''}{sec?.label}</div>
          <button type="button" aria-label={picked ? 'Remove pick' : 'Pick'} onClick={() => onTogglePick(item)}
            style={{ width: 36, height: 36, borderRadius: '50%', border: `1px solid ${E.line2}`, background: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
            <Heart size={16} color={picked ? E.pink : E.ink} fill={picked ? E.pink : 'none'} />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: isMobile ? 16 : 24 }}>
          {images.length > 0 && (
            <div style={{ position: 'relative', marginBottom: 20 }}>
              <div style={{ aspectRatio: isMobile ? '4 / 3' : '16 / 7', borderRadius: 12, overflow: 'hidden', background: E.studio }}>
                <img src={images[photo]} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
              {images.length > 1 && (
                <>
                  <button type="button" aria-label="Previous photo" onClick={() => setPhoto((p) => (p - 1 + images.length) % images.length)}
                    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,.92)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><ChevronLeft size={16} /></button>
                  <button type="button" aria-label="Next photo" onClick={() => setPhoto((p) => (p + 1) % images.length)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,.92)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><ChevronRight size={16} /></button>
                  <span style={{ position: 'absolute', right: 12, bottom: 12, background: 'rgba(0,0,0,.6)', color: '#fff', borderRadius: 40, padding: '3px 10px', fontSize: 12 }}>{photo + 1} / {images.length}</span>
                </>
              )}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: isMobile ? 20 : 40, alignItems: 'start' }}>
            <div>
              {(item.tags || []).length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>{item.tags.map((t) => <Tag key={t}>{t}</Tag>)}</div>}
              <h2 style={{ fontFamily: E.serif, fontSize: isMobile ? 30 : 36, fontWeight: 500, margin: '0 0 6px', lineHeight: 1.1 }}>{item.title}</h2>
              {(item.place || item.capacity || item.slot) && (
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14, color: E.sub, marginBottom: 10 }}>
                  {item.place && <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><MapPin size={14} /> {item.place}</span>}
                  {item.capacity && <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><Users size={14} /> {item.capacity}</span>}
                  {item.slot && <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><Clock size={14} /> {item.slot}</span>}
                </div>
              )}
              {item.tagline && <p style={{ fontSize: 16, color: E.sub, margin: '0 0 8px', lineHeight: 1.55 }}>{item.tagline}</p>}
              {item.description && <p style={{ fontSize: 15, color: E.sub, margin: 0, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{item.description}</p>}

              {videos.length > 0 && (
                <>
                  <div style={{ height: 1, background: E.line, margin: '20px 0' }} />
                  <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 12 }}>Videos</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16 }}>
                    {videos.map((v) => <EventVideo key={v._id} video={v} item={item} slug={slug} />)}
                  </div>
                </>
              )}
              {(item.schedule || []).length > 0 && (
                <>
                  <div style={{ height: 1, background: E.line, margin: '20px 0' }} />
                  <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 12 }}>Run of show</div>
                  <Timeline schedule={item.schedule} compact={isMobile} />
                </>
              )}
              {(item.includes || []).length > 0 && (
                <>
                  <div style={{ height: 1, background: E.line, margin: '20px 0' }} />
                  <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 12 }}>{item.section === 'fnb' ? 'On the menu' : item.section === 'venue' ? 'Setup' : "What's included"}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px 20px' }}>
                    {item.includes.map((x) => <div key={x} style={{ display: 'flex', gap: 10, fontSize: 15 }}><Check size={18} style={{ flexShrink: 0 }} /> <span>{x}</span></div>)}
                  </div>
                </>
              )}
            </div>
            <div style={{ border: `1px solid ${E.line2}`, borderRadius: 12, padding: 20, boxShadow: '0 6px 16px rgba(0,0,0,.08)', position: isMobile ? 'static' : 'sticky', top: 0 }}>
              <PriceLine item={item} size={20} />
              {item.note && (
                <div style={{ marginTop: 14, padding: '12px 14px', background: E.studio, borderRadius: 8, fontSize: 14, lineHeight: 1.5 }}>
                  <div style={{ fontSize: 12, color: E.muted, marginBottom: 4 }}>From your planner</div>{item.note}
                </div>
              )}
              <button type="button" onClick={() => onTogglePick(item)} style={{ ...pickBtn(picked), width: '100%', minHeight: 48, marginTop: 16, fontSize: 15 }}>
                {picked ? <Check size={16} /> : <Heart size={16} />} {pickLabel}
              </button>
              {picked && <div style={{ fontSize: 13, color: E.muted, textAlign: 'center', marginTop: 8 }}>Tap again to remove it from your picks.</div>}
              <button type="button" onClick={() => onAsk(item)} style={{ ...pickBtn(false), width: '100%', minHeight: 48, marginTop: 10, fontSize: 15, borderColor: E.line2 }}>
                <MessageCircle size={16} /> Ask a question
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Event plan ───────────────────────────────────────────────────────────────
const SectionHead = ({ s, n, extra }) => (
  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: E.goldText, display: 'inline-flex', alignItems: 'center', gap: 6 }}><s.icon size={14} /> {String(n).padStart(2, '0')} · {s.label}</div>
      <h2 style={{ fontFamily: E.serif, fontSize: 'clamp(28px,4vw,36px)', fontWeight: 500, margin: '4px 0 4px', lineHeight: 1.1 }}>{s.long}</h2>
      <p style={{ fontSize: 15, color: E.muted, margin: 0 }}>{s.sub}</p>
    </div>
    {extra}
  </div>
);

const OptionCard = ({ item, idx, picked, onOpen, onTogglePick }) => {
  const videos = (item.videos || []).length;
  return (
    <div onClick={() => onOpen(item, idx)} style={{ cursor: 'pointer', fontFamily: E.sans }}>
      <Photo item={item}>
        {picked ? <PickedBadge /> : <span style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(255,255,255,.92)', borderRadius: 40, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>Option {String(idx + 1).padStart(2, '0')}</span>}
        <HeartBtn on={picked} onClick={() => onTogglePick(item)} label={picked ? `Remove ${item.title} from your picks` : `Pick ${item.title}`} />
        <VideoBadge n={videos} />
      </Photo>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>{item.title}</div>
        {item.place && <div style={{ fontSize: 14, color: E.muted }}>{item.place}{item.capacity ? ` · ${item.capacity}` : ''}</div>}
        {item.tagline && <div style={{ fontSize: 14, color: E.muted, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.tagline}</div>}
        {item.slot && <div style={{ fontSize: 13, color: E.muted, marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={12} /> {item.slot}</div>}
        <div style={{ marginTop: 6 }}><PriceLine item={item} /></div>
      </div>
    </div>
  );
};

/** Run-of-show options: switch between them, the chosen one laid out as a timeline. */
const FlowSection = ({ list, picks, onTogglePick, onOpen, isMobile }) => {
  const firstPicked = list.findIndex((i) => picks.includes(String(i._id)));
  const [shown, setShown] = useState(firstPicked >= 0 ? firstPicked : 0);
  const item = list[Math.min(shown, list.length - 1)];
  const picked = picks.includes(String(item._id));
  return (
    <div>
      {list.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {list.map((o, i) => {
            const on = i === shown;
            return (
              <button key={o._id} type="button" onClick={() => setShown(i)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 40, cursor: 'pointer', border: `1px solid ${on ? E.ink : E.line2}`, background: on ? E.ink : '#fff', color: on ? '#fff' : E.ink, fontFamily: E.sans, fontSize: 13, fontWeight: 500 }}>
                Option {i + 1} · {o.title}{picks.includes(String(o._id)) && <Check size={14} />}
              </button>
            );
          })}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 300px', gap: 28, border: `1px solid ${E.line}`, borderRadius: 16, padding: isMobile ? 16 : 28 }}>
        {(item.schedule || []).length
          ? <Timeline schedule={item.schedule} compact={isMobile} />
          : <p style={{ fontSize: 15, color: E.sub, margin: 0, lineHeight: 1.6 }}>{item.description || item.tagline}</p>}
        <aside>
          <Photo item={item} ratio="4 / 3" onClick={() => onOpen(item, shown)} />
          <div style={{ fontFamily: E.serif, fontSize: 24, marginTop: 12 }}>{item.title}</div>
          {item.tagline && <div style={{ fontSize: 14, color: E.muted, margin: '4px 0 10px' }}>{item.tagline}</div>}
          <div style={{ marginBottom: 12 }}><PriceLine item={item} /></div>
          <button type="button" onClick={() => onTogglePick(item)} style={{ ...pickBtn(picked), width: '100%' }}>
            {picked ? <><Check size={15} /> Picked for your event</> : 'Pick this plan'}
          </button>
          <button type="button" onClick={() => onOpen(item, shown)} style={{ marginTop: 10, width: '100%', background: 'none', border: 'none', textDecoration: 'underline', fontFamily: E.sans, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: E.ink }}>Show details</button>
        </aside>
      </div>
    </div>
  );
};

const AddonRow = ({ item, on, guests, onToggle, onOpen }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderTop: `1px solid ${E.line}`, fontFamily: E.sans }}>
    <button type="button" role="switch" aria-checked={on} aria-label={`${item.title} add-on`} onClick={() => onToggle(item)}
      style={{ width: 44, height: 26, borderRadius: 20, border: 'none', cursor: 'pointer', background: on ? E.ink : E.line2, position: 'relative', flexShrink: 0, transition: 'background .15s' }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
    </button>
    <button type="button" onClick={() => onOpen(item)} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: E.sans, color: E.ink }}>
      <div style={{ fontSize: 15, fontWeight: 500 }}>{item.title}</div>
      <div style={{ fontSize: 13, color: E.muted }}>{item.unit === 'included' ? 'Included' : `${inr(item.price)} ${item.unit === 'perGuest' ? 'per guest' : 'flat'}`}{item.tagline ? ` · ${item.tagline}` : ''}</div>
    </button>
    {guests != null && <b style={{ fontSize: 15 }}>{item.unit === 'included' ? 'Included' : inr(lineCost(item, guests))}</b>}
  </div>
);

export const EventPlan = ({ portal, picks, onTogglePick, onOpen, onOpenBudget, isMobile, isTablet }) => {
  const items = useMemo(() => sortItems(portal.eventItems), [portal.eventItems]);
  const info = portal.eventInfo || {};
  const sections = EVENT_SECTIONS.filter((s) => items.some((i) => i.section === s.key));
  const pickedSections = sections.filter((s) => items.some((i) => i.section === s.key && !i.addon && picks.includes(String(i._id)))).length;
  const [active, setActive] = useState(sections[0]?.key);
  const refs = useRef({});

  useEffect(() => {
    const els = Object.values(refs.current).filter(Boolean);
    if (!els.length || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      const top = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (top) setActive(top.target.dataset.section);
    }, { rootMargin: '-180px 0px -55% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [sections.length]);

  const jump = (key) => {
    const el = refs.current[key];
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 190, behavior: 'smooth' });
  };

  // Hero mosaic: the cover, then the first photo of each themes / venue option.
  const mosaic = [info.coverUrl, ...items.filter((i) => i.images?.[0]).map((i) => i.images[0])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).slice(0, 5);
  const guests = portal.eventGuests || info.guests || 0;
  const facts = [
    info.date && { icon: CalendarDays, k: 'When', v: info.date },
    guests && { icon: Users, k: 'Guests', v: String(guests) },
    info.startFrom && { icon: Navigation, k: 'Starting from', v: info.startFrom },
    info.format && { icon: Layers, k: 'Format', v: info.format },
  ].filter(Boolean);

  if (!items.length) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 16px', fontFamily: E.sans }}>
        <Sparkles size={32} color={E.goldText} />
        <h2 style={{ fontFamily: E.serif, fontSize: 32, fontWeight: 400, margin: '12px 0 8px' }}>Your event plan is on its way</h2>
        <p style={{ color: E.muted, fontSize: 15 }}>The Marqland team is putting your options together. Check back shortly.</p>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: E.sans, color: E.ink }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 40, background: E.studio, fontSize: 13, fontWeight: 500 }}>
        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: E.gold }} /> Corporate Event · {portal.orderRef}
      </span>
      <h1 style={{ fontFamily: E.serif, fontSize: isMobile ? 'clamp(32px,8vw,44px)' : 'clamp(40px,5vw,60px)', fontWeight: 500, lineHeight: 1.05, margin: '14px 0 8px' }}>
        {portal.title || <>Curated for <em style={{ color: E.goldText }}>{portal.clientName}.</em></>}
      </h1>
      <p style={{ fontSize: isMobile ? 15 : 17, color: E.sub, maxWidth: 720, lineHeight: 1.55, margin: 0 }}>
        {portal.teamNote || 'Pick one option in each section. Your planner sees your picks right away.'}
      </p>

      {mosaic.length > 0 && (
        isMobile || mosaic.length < 3
          ? <div style={{ marginTop: 20, aspectRatio: '16 / 10', borderRadius: 12, overflow: 'hidden' }}><img src={mosaic[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>
          : (
            <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 8, height: isTablet ? 300 : 400, borderRadius: 16, overflow: 'hidden' }}>
              {mosaic.map((src, i) => (
                <div key={src} style={{ gridRow: i === 0 ? '1 / 3' : 'auto', gridColumn: mosaic.length === 3 && i > 0 ? '2 / 4' : 'auto', overflow: 'hidden', background: E.studio }}>
                  <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
              ))}
            </div>
          )
      )}

      {facts.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isMobile ? 2 : facts.length}, 1fr)`, gap: 0, border: `1px solid ${E.line2}`, borderRadius: 12, marginTop: 20, overflow: 'hidden' }}>
          {facts.map((f, i) => (
            <div key={f.k} style={{ padding: '14px 18px', borderLeft: (!isMobile && i > 0) || (isMobile && i % 2) ? `1px solid ${E.line2}` : 'none', borderTop: isMobile && i > 1 ? `1px solid ${E.line2}` : 'none', display: 'flex', gap: 12, alignItems: 'center' }}>
              <f.icon size={20} strokeWidth={1.6} />
              <div><div style={{ fontSize: 12, color: E.muted }}>{f.k}</div><div style={{ fontSize: 15, fontWeight: 600 }}>{f.v}</div></div>
            </div>
          ))}
        </div>
      )}

      {/* Sticky section bar */}
      <div style={{ position: 'sticky', top: isMobile ? 112 : 122, zIndex: 30, background: '#fff', borderBottom: `1px solid ${E.line}`, margin: '24px 0 0', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ display: 'flex', gap: isMobile ? 20 : 32, overflowX: 'auto', scrollbarWidth: 'none', flex: 1 }}>
          {sections.map((s) => {
            const on = active === s.key;
            const done = items.some((i) => i.section === s.key && !i.addon && picks.includes(String(i._id)));
            return (
              <button key={s.key} type="button" onClick={() => jump(s.key)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '12px 2px', background: 'none', border: 'none', borderBottom: `2px solid ${on ? E.ink : 'transparent'}`, color: on ? E.ink : E.muted, cursor: 'pointer', fontFamily: E.sans, fontSize: 12, fontWeight: on ? 600 : 500, whiteSpace: 'nowrap', position: 'relative' }}>
                <s.icon size={22} strokeWidth={1.6} />
                <span>{s.label}</span>
                {done && <span aria-label="Picked" style={{ position: 'absolute', top: 6, right: -6, width: 16, height: 16, borderRadius: '50%', background: E.ink, color: '#fff', display: 'grid', placeItems: 'center' }}><Check size={10} /></span>}
              </button>
            );
          })}
        </div>
        {!isMobile && (
          <button type="button" onClick={onOpenBudget} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, background: 'none', border: 'none', cursor: onOpenBudget ? 'pointer' : 'default', fontFamily: E.sans, color: E.ink }}>
            <b>{pickedSections} of {sections.length} picked</b>
            <span style={{ width: 80, height: 4, borderRadius: 4, background: E.line, overflow: 'hidden' }}><span style={{ display: 'block', height: '100%', width: `${sections.length ? (pickedSections / sections.length) * 100 : 0}%`, background: E.ink }} /></span>
          </button>
        )}
      </div>

      {sections.map((s, n) => {
        const all = items.filter((i) => i.section === s.key);
        const list = all.filter((i) => !i.addon);
        const addons = all.filter((i) => i.addon);
        const indexOf = (item) => list.indexOf(item);
        return (
          <section key={s.key} data-section={s.key} ref={(el) => { refs.current[s.key] = el; }} style={{ padding: isMobile ? '36px 0 8px' : '56px 0 8px', borderBottom: n < sections.length - 1 ? `1px solid ${E.line}` : 'none' }}>
            <SectionHead s={s} n={EVENT_SECTIONS.indexOf(s) + 1}
              extra={list.length > 1 ? <Tag>{MULTI_PICK.has(s.key) ? 'Pick as many as you like' : 'Pick one'}</Tag> : null} />
            {s.key === 'flow' && list.length > 0
              ? <FlowSection list={list} picks={picks} onTogglePick={onTogglePick} onOpen={onOpen} isMobile={isMobile} />
              : list.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : s.key === 'venue' ? 'repeat(auto-fill, minmax(340px, 1fr))' : 'repeat(auto-fill, minmax(240px, 1fr))', gap: isMobile ? '28px 0' : '36px 24px' }}>
                  {list.map((item) => (
                    <OptionCard key={item._id} item={item} idx={indexOf(item)} picked={picks.includes(String(item._id))} onOpen={onOpen} onTogglePick={onTogglePick} />
                  ))}
                </div>
              )}
            {addons.length > 0 && (
              <div style={{ marginTop: 28, maxWidth: 720 }}>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Add-ons</div>
                {addons.map((a) => <AddonRow key={a._id} item={a} on={picks.includes(String(a._id))} onToggle={onTogglePick} onOpen={(it) => onOpen(it, null)} />)}
              </div>
            )}
            {all.filter((i) => i.note).length > 0 && s.key !== 'flow' && list.length === 1 && (
              <div style={{ marginTop: 20, padding: '14px 18px', background: E.studio, borderRadius: 12, fontSize: 14, maxWidth: 720 }}>
                <div style={{ fontSize: 12, color: E.muted, marginBottom: 4 }}>From your Marqland planner</div>{list[0].note}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

// ── Budget ───────────────────────────────────────────────────────────────────
export const EventBudget = ({ portal, picks, guests, onGuests, onTogglePick, onChange, onSend, isMobile }) => {
  const items = useMemo(() => sortItems(portal.eventItems), [portal.eventItems]);
  const byId = new Map(items.map((i) => [String(i._id), i]));
  const picked = picks.map((id) => byId.get(id)).filter(Boolean);
  const main = EVENT_SECTIONS.flatMap((s) => picked.filter((i) => i.section === s.key && !i.addon));
  const addons = items.filter((i) => i.addon);
  const pickedAddons = addons.filter((a) => picks.includes(String(a._id)));
  const total = [...main, ...pickedAddons].reduce((sum, i) => sum + lineCost(i, guests), 0);
  const bySection = EVENT_SECTIONS.map((s) => ({ s, amount: main.filter((i) => i.section === s.key).reduce((sum, i) => sum + lineCost(i, guests), 0), n: main.filter((i) => i.section === s.key).length }))
    .filter((r) => r.n);
  const addonTotal = pickedAddons.reduce((sum, i) => sum + lineCost(i, guests), 0);
  const missing = EVENT_SECTIONS.filter((s) => items.some((i) => i.section === s.key && !i.addon) && !main.some((i) => i.section === s.key));

  const stepBtn = { width: 34, height: 34, borderRadius: '50%', border: `1px solid ${E.line2}`, background: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center' };
  return (
    <div style={{ fontFamily: E.sans, color: E.ink }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: E.goldText }}>Budget</div>
      <h1 style={{ fontFamily: E.serif, fontSize: isMobile ? 34 : 48, fontWeight: 500, lineHeight: 1.08, margin: '6px 0 8px' }}>See what your picks add up to</h1>
      <p style={{ color: E.sub, fontSize: 16, maxWidth: 640, lineHeight: 1.55, margin: '0 0 28px' }}>Change the guest count or swap a pick and the total updates. Per-guest items scale with your headcount.</p>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 360px', gap: isMobile ? 24 : 48, alignItems: 'start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0 18px', borderBottom: `1px solid ${E.line}` }}>
            <div><b style={{ fontSize: 16 }}>Guests</b><div style={{ fontSize: 13, color: E.muted }}>Used for per-guest items</div></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button type="button" aria-label="Fewer guests" style={stepBtn} onClick={() => onGuests(Math.max(0, guests - 10))}><Minus size={14} /></button>
              <input aria-label="Guests" inputMode="numeric" value={guests || ''} onChange={(e) => onGuests(Math.min(100000, Number(e.target.value.replace(/[^0-9]/g, '')) || 0))}
                style={{ width: 70, minHeight: 36, textAlign: 'center', fontSize: 16, fontWeight: 600 }} />
              <button type="button" aria-label="More guests" style={stepBtn} onClick={() => onGuests(guests + 10)}><Plus size={14} /></button>
            </div>
          </div>

          {main.map((i) => {
            const s = SECTION[i.section];
            return (
              <div key={i._id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 0', borderBottom: `1px solid ${E.line}` }}>
                <span style={{ width: 40, height: 40, borderRadius: '50%', background: E.studio, display: 'grid', placeItems: 'center', flexShrink: 0 }}><s.icon size={17} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: E.muted }}>{s.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{i.title}</div>
                  <button type="button" onClick={() => onChange(i.section)} style={{ background: 'none', border: 'none', padding: 0, textDecoration: 'underline', fontSize: 13, cursor: 'pointer', color: E.ink, fontFamily: E.sans }}>Change</button>
                  {MULTI_PICK.has(i.section) && <button type="button" onClick={() => onTogglePick(i)} style={{ background: 'none', border: 'none', padding: '0 0 0 12px', textDecoration: 'underline', fontSize: 13, cursor: 'pointer', color: E.muted, fontFamily: E.sans }}>Remove</button>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <b style={{ fontSize: 15 }}>{i.unit === 'included' ? 'Included' : inr(lineCost(i, guests))}</b>
                  <div style={{ fontSize: 12, color: E.muted }}>{i.unit === 'perGuest' ? `${inr(i.price)} × ${guests}` : i.unit === 'flat' ? 'flat' : ''}</div>
                </div>
              </div>
            );
          })}
          {missing.map((s) => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 0', borderBottom: `1px solid ${E.line}`, color: E.muted }}>
              <span style={{ width: 40, height: 40, borderRadius: '50%', border: `1px dashed ${E.line2}`, display: 'grid', placeItems: 'center', flexShrink: 0 }}><s.icon size={17} /></span>
              <div style={{ flex: 1 }}><div style={{ fontSize: 12 }}>{s.label}</div><div style={{ fontSize: 15 }}>Not picked yet</div></div>
              <button type="button" onClick={() => onChange(s.key)} style={{ ...pickBtn(false), minHeight: 36, fontSize: 13 }}>Pick</button>
            </div>
          ))}

          {addons.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <b style={{ fontSize: 15 }}>Add-ons</b>
              {addons.map((a) => <AddonRow key={a._id} item={a} on={picks.includes(String(a._id))} guests={guests} onToggle={onTogglePick} onOpen={() => onChange(a.section)} />)}
            </div>
          )}
        </div>

        <div style={{ border: `1px solid ${E.line2}`, borderRadius: 12, padding: 24, boxShadow: '0 6px 16px rgba(0,0,0,.12)', position: isMobile ? 'static' : 'sticky', top: 140 }}>
          <div style={{ fontSize: 13, color: E.muted }}>Estimated total</div>
          <div style={{ fontSize: 34, fontWeight: 600, margin: '6px 0 4px' }}>{inr(total)}</div>
          <div style={{ fontSize: 14, color: E.muted, marginBottom: 14 }}>{guests ? `${inr(total / guests)} per guest · ` : ''}before GST</div>
          {bySection.map(({ s, amount }) => (
            <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: `1px solid ${E.line}`, fontSize: 14 }}><span>{s.label}</span><b>{amount ? inr(amount) : 'Included'}</b></div>
          ))}
          {pickedAddons.length > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: `1px solid ${E.line}`, fontSize: 14 }}><span>Add-ons</span><b>{inr(addonTotal)}</b></div>}
          <button type="button" onClick={() => onSend({ main, addons: pickedAddons, total, guests })} disabled={!main.length && !pickedAddons.length}
            style={{ ...pickBtn(true), width: '100%', minHeight: 48, marginTop: 16, fontSize: 15, opacity: main.length || pickedAddons.length ? 1 : 0.4 }}>
            <Send size={15} /> Send picks to planner
          </button>
          <div style={{ fontSize: 12.5, color: E.muted, marginTop: 12, lineHeight: 1.5 }}>Indicative only. The final quote includes GST, travel and any changes agreed on the message board.</div>
        </div>
      </div>
    </div>
  );
};

/** The message the client sends from the Budget tab. */
export const picksMessage = ({ main, addons, total, guests }) => [
  `Our picks for ${guests || 'our'} guests:`,
  ...main.map((i) => `• ${SECTION[i.section]?.label}: ${i.title}`),
  ...addons.map((i) => `• Add-on: ${i.title}`),
  `Estimated total: ${inr(total)} before GST`,
].join('\n');
